/**
 * Pure helpers behind `ui/Field` and `FormErrorSummary` (T-43, F-029).
 * Kept free of React so the rules are unit-tested.
 */

/** A form's errors by field name; empty / undefined means "no error". */
export type FieldErrors = Record<string, string | null | undefined | false>;

/** Stable ids for the parts of one field. */
export function fieldIds(id: string) {
  return { control: id, hint: `${id}-hint`, error: `${id}-error` };
}

/** `aria-describedby` for a control: the error first (read first), then the hint; undefined when neither. */
export function describedBy(id: string, opts: { hint?: boolean; error?: boolean; extra?: string }): string | undefined {
  const ids = fieldIds(id);
  const parts = [opts.error && ids.error, opts.hint && ids.hint, opts.extra].filter(Boolean) as string[];
  return parts.length ? parts.join(' ') : undefined;
}

/** Field names that currently have an error, in `order` (the visual order of the form), then any others. */
export function errorKeys(errors: FieldErrors, order: readonly string[] = []): string[] {
  const has = (k: string) => Boolean(errors[k]);
  const ordered = order.filter(has);
  const rest = Object.keys(errors).filter((k) => has(k) && !order.includes(k));
  return [...ordered, ...rest];
}

/** The first field to focus after a failed submit, or null when the form is valid. */
export function firstErrorKey(errors: FieldErrors, order: readonly string[] = []): string | null {
  return errorKeys(errors, order)[0] ?? null;
}

/** True when the form has at least one error. */
export function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some(Boolean);
}

/* ── Validation rules (T-43): pure, return an i18n key, never a string ─────── */

/** One validation finding: an i18n key plus its interpolation params. */
export interface FieldIssue {
  key: string;
  params?: Record<string, string | number>;
}

/** A rule sees the field's value and every value of the form (for cross-field rules). */
export type FieldRule = (value: unknown, values: Record<string, unknown>) => FieldIssue | null;

/** Field id → its rules, in the form's visual order. */
export type FormSchema = Record<string, readonly FieldRule[]>;

/** Field id → its current issue (absent / null = valid). */
export type FieldIssues = Record<string, FieldIssue | null | undefined>;

const text = (v: unknown): string => (v == null ? '' : String(v));
const blank = (v: unknown): boolean => (Array.isArray(v) ? v.length === 0 : text(v).trim() === '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The rule set the T-43 forms use. Every rule except `required` passes an empty value. */
export const rules = {
  required: (key = 'validation.required'): FieldRule => (v) => (blank(v) ? { key } : null),
  minLength: (min: number, key = 'validation.minLength'): FieldRule => (v) => {
    const s = text(v).trim();
    return s && s.length < min ? { key, params: { min } } : null;
  },
  maxLength: (max: number, key = 'validation.maxLength'): FieldRule => (v) =>
    (text(v).length > max ? { key, params: { max } } : null),
  email: (key = 'validation.invalidEmail'): FieldRule => (v) => {
    const s = text(v).trim();
    return s && !EMAIL_RE.test(s) ? { key } : null;
  },
  /** Accepts a bare host ("example.co.il"), exactly as the organization form always did. */
  url: (key = 'validation.invalidUrl'): FieldRule => (v) => {
    const s = text(v).trim();
    if (!s) return null;
    try {
      new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
      return null;
    } catch {
      return { key };
    }
  },
  /** A number greater than `min` (hours, money). Accepts "1.5" and "1,5". */
  numberAbove: (min: number, key = 'form.numberAbove'): FieldRule => (v) => {
    const s = text(v).trim();
    if (!s) return null;
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) && n > min ? null : { key, params: { min } };
  },
  /** This date/time is later than the one in `otherId` (same `datetime-local` / ISO format). */
  after: (otherId: string, key = 'form.endAfterStart'): FieldRule => (v, values) => {
    const a = Date.parse(text(v));
    const b = Date.parse(text(values[otherId]));
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    return a > b ? null : { key };
  },
};

/** The first failing rule of one field, or null. */
export function validateField(schema: FormSchema, id: string, values: Record<string, unknown>): FieldIssue | null {
  for (const r of schema[id] ?? []) {
    const issue = r(values[id], values);
    if (issue) return issue;
  }
  return null;
}

/** Every field of the schema (on submit). */
export function validateAll(schema: FormSchema, values: Record<string, unknown>): FieldIssues {
  const out: FieldIssues = {};
  for (const id of Object.keys(schema)) {
    const issue = validateField(schema, id, values);
    if (issue) out[id] = issue;
  }
  return out;
}

/** True when at least one field has an issue. */
export function hasIssues(issues: FieldIssues): boolean {
  return Object.values(issues).some(Boolean);
}

/**
 * On blur: validate the field the user just left. An empty field that has no
 * error yet stays quiet (a required field is flagged on submit, not on a tab
 * through the form). Returns `prev` itself when nothing changed.
 */
export function issuesAfterBlur(prev: FieldIssues, schema: FormSchema, id: string, values: Record<string, unknown>): FieldIssues {
  if (!(id in schema)) return prev;
  if (blank(values[id]) && !prev[id]) return prev;
  const issue = validateField(schema, id, values);
  if (sameIssue(prev[id], issue)) return prev;
  const next = { ...prev };
  if (issue) next[id] = issue; else delete next[id];
  return next;
}

/**
 * While typing: never add an error, only clear (or update) one that is already
 * shown once its field becomes valid. Re-checks every shown error, so fixing
 * the start time also clears "the end must be after the start".
 * Returns `prev` itself when nothing changed (safe to call on every render).
 */
export function issuesAfterChange(prev: FieldIssues, schema: FormSchema, values: Record<string, unknown>): FieldIssues {
  let next: FieldIssues | null = null;
  for (const id of Object.keys(prev)) {
    if (!prev[id]) continue;
    const issue = validateField(schema, id, values);
    if (sameIssue(prev[id], issue)) continue;
    next = next ?? { ...prev };
    if (issue) next[id] = issue; else delete next[id];
  }
  return next ?? prev;
}

function sameIssue(a: FieldIssue | null | undefined, b: FieldIssue | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.key === b.key && JSON.stringify(a.params ?? null) === JSON.stringify(b.params ?? null);
}

/* ── Server errors (T-43, PRD §4.17, NL-12 / B9) ───────────────────────────── */

export type ServerErrorKind =
  | 'message' | 'forbidden' | 'notFound' | 'conflict' | 'rateLimited' | 'network' | 'server' | 'unknown'
  | 'validation' | 'duplicate' | 'periodLocked';

export interface ServerErrorView {
  kind: ServerErrorKind;
  /** The server's own Hebrew sentence, shown as the main line (kind `message`). */
  message?: string;
  /** "HTTP 409 · PERIOD_LOCKED · raw text", for the "פרטים טכניים" disclosure. */
  detail?: string;
}

const HEBREW_RE = /[֐-׿]/;

/**
 * The server's stable `error.error_code` (B9, NL-12) → the kind whose Hebrew
 * line `FormServerError` shows (`form.server.<kind>`). Unknown codes (business
 * rules such as HOUR_BANK_EXHAUSTED) fall back to the message / status rules.
 */
export const ERROR_CODE_KINDS: Readonly<Record<string, ServerErrorKind>> = {
  VALIDATION: 'validation',
  INVALID_REFERENCE: 'validation',
  DUPLICATE: 'duplicate',
  CONFLICT: 'conflict',
  PERIOD_LOCKED: 'periodLocked',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'notFound',
  RATE_LIMITED: 'rateLimited',
  INTERNAL: 'server',
};

/**
 * Codes whose Hebrew line always wins over the server's text: the server's
 * sentence adds nothing ("הרשומה כבר קיימת במערכת", the limiter's notice).
 * For the other codes a specific Hebrew sentence from the server (which
 * months are locked, which field is invalid, why access is denied) is kept,
 * and the code's line is used only when there is no such sentence.
 */
const CODE_TEXT_WINS: ReadonlySet<string> = new Set(['DUPLICATE', 'RATE_LIMITED', 'INTERNAL']);

/**
 * How to show a rejected API call (the `api` client rejects with the API body
 * plus `response.status`):
 * 1. a network failure → `network`;
 * 2. a known stable code (`error.error_code`, B9) → its kind, unless the
 *    server wrote a specific Hebrew sentence for it (see `CODE_TEXT_WINS`);
 * 3. no code (an older server, a route that answers by itself): the server's
 *    Hebrew sentence as it is, else a Hebrew line by the HTTP status.
 * The raw text and the code always go to the technical details.
 */
export function serverErrorView(err: unknown): ServerErrorView {
  if (!err) return { kind: 'unknown' };
  const e = (typeof err === 'object' ? err : { message: String(err) }) as {
    error?: { message?: string; code?: number; error_code?: string } | string;
    message?: string;
    code?: string | number;
    status?: number;
    response?: { status?: number };
  };
  const body = typeof e.error === 'object' && e.error ? e.error : null;
  const raw = (body?.message || (typeof e.error === 'string' ? e.error : '') || e.message || '').trim();
  const status = e.response?.status ?? (typeof body?.code === 'number' ? body.code : undefined) ?? e.status;
  const errorCode = typeof body?.error_code === 'string' ? body.error_code : undefined;
  const detailParts = [status ? `HTTP ${status}` : null, errorCode || null];

  const network = !status && (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || /network error|failed to fetch|timeout/i.test(raw));
  if (network) return { kind: 'network', detail: join([...detailParts, raw]) };

  const hebrew = !!raw && HEBREW_RE.test(raw) && (!status || status < 500);
  const codeKind = errorCode ? ERROR_CODE_KINDS[errorCode] : undefined;
  if (codeKind && (CODE_TEXT_WINS.has(errorCode!) || !hebrew)) {
    return { kind: codeKind, detail: join([...detailParts, raw]) };
  }

  if (hebrew) {
    return { kind: 'message', message: raw, detail: join(detailParts) };
  }
  const detail = join([...detailParts, raw]);
  if (status === 403) return { kind: 'forbidden', detail };
  if (status === 404) return { kind: 'notFound', detail };
  if (status === 409) return { kind: 'conflict', detail };
  if (status === 429) return { kind: 'rateLimited', detail };
  if (status && status >= 500) return { kind: 'server', detail };
  return { kind: 'unknown', detail };
}

function join(parts: (string | null | undefined)[]): string | undefined {
  const s = parts.filter(Boolean).join(' · ');
  return s || undefined;
}
